// PricingComparisonTable.jsx
import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import images from "../../../Utils/images";


const YES_COLOR = "#7755F8";   
const NO_COLOR = "#FF4D4D";    

const Yes = () => (
  <CheckCircleRoundedIcon sx={{ color: YES_COLOR }} fontSize="small" />
);
const No = () => <CancelRoundedIcon sx={{ color: NO_COLOR }} fontSize="small" />;


const LOGOS = {
  WhatsApp: images.Logo_Whatsapp,
  Gmail: images.Logo_Gmail,
  SMS: images.Logo_SMS,
  Instagram: images.Logo_Instagram,
  Facebook: images.Logo_Facebook,
};

const ChannelIcons = ({ items = [] }) => (
  <Stack
    direction="row"
    gap={2}
    flexWrap="wrap"
    sx={{ justifyContent: { xs: "flex-start", md: "center" } }}
  >
    {items.map((x) => (
      <img
        key={x}
        src={LOGOS[x]}
        alt={x}
        style={{ width: 30, height: 30, objectFit: "contain", padding:2,margin:2 }}
      />
    ))}
  </Stack>
);


const COLUMNS = ["STARTUP", "GROWTH", "PRO"];
const CHANNELS = ["WhatsApp", "Gmail", "SMS", "Instagram",];

const SECTIONS = [
  {
    title: "",
    rows: [
      { feature: "Channels", render: () => <ChannelIcons items={CHANNELS} /> },
      { feature: "WhatsApp Automation & Chatbot", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Instagram Automation & Chatbot", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "AI Agents (As per model usage)", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "WhatsApp Team Inbox", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Instagram Team Inbox", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Agent Login (₹500/Add-on)", STARTUP: "2", GROWTH: "4", PRO: "8" },
      { feature: "Meta Ads Manager", STARTUP: <No />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Email Manager & Automation", STARTUP: "1 Sender ID", GROWTH: "3 Sender IDs", PRO: "5 Sender IDs" },
      { feature: "Email Sequences & Warmup", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Embedded Forms", STARTUP: "With watermark", GROWTH: "With watermark", PRO: "Without watermark" },
      { feature: "Advanced Analytics & Reports", STARTUP: <No />, GROWTH: "All Reports", PRO: "All Reports" },
      { feature: "Mobile App Inbox", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Integrations", STARTUP: "Unlimited", GROWTH: "Unlimited", PRO: "Unlimited" },
    ],
  },
  {
    title: "Support",
    rows: [
      { feature: "Onboarding Support", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Customer Support", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
      { feature: "Tech Support", STARTUP: <No />, GROWTH: <No />, PRO: <Yes /> },
    ],
  },
  {
    title: "Developer Tools",
    rows: [
      { feature: "Developer API Library", STARTUP: <No />, GROWTH: <No />, PRO: <Yes /> },
      { feature: "Webhook Integration", STARTUP: <Yes />, GROWTH: <Yes />, PRO: <Yes /> },
    ],
  },
];


export default function PricingComparisonTable() {
  return (
    <div className="container my-5">
      {/* <div className="text-center mb-4">
        <h2 className="fw-bold mb-2">Compare Plans</h2>
        <p className="text-muted mb-0">
          A quick feature comparison of Startup, Growth, and Pro.
          <InfoOutlinedIcon fontSize="small" className="ms-1" />
        </p>
      </div> */}

      <TableContainer
        component={Paper}
        className="shadow-sm"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Table aria-label="pricing comparison" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: "#FAFCFC",
                  fontWeight: 700,
                  position: { md: "sticky" },
                  left: { md: 0 },
                  zIndex: { md: 2 },
                  px: 3,
                  py: 2.25,
                  minWidth: 260,
                }}
              >
                Features
              </TableCell>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col}
                  align="center"
                  sx={{
                    backgroundColor: "#FAFCFC",
                    fontWeight: 700,
                    px: 3,
                    py: 2.25,
                    minWidth: 200,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {SECTIONS.map((section, si) => (
              <React.Fragment key={section.title}>
                 {si > 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length + 2}
                      sx={{ backgroundColor: "white", height: 60, border: "none" }}
                    />
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length + 1}
                    sx={{
                      backgroundColor: "#FAFCFC",
                      fontWeight: 700,
                      px: 3,
                      py: 2,
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-2">{section.title}</span>
                      <Divider className="flex-grow-1" />
                    </div>
                  </TableCell>
                </TableRow>

                {section.rows.map((row, ri) => (
                  <TableRow
                    key={`${si}-${ri}`}
                    hover
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "#FAFCFC"},
                      "&:nth-of-type(odd)": { backgroundColor: "#FAFCFC"},
                    }}
                  >
                    <TableCell
                      sx={{
                        position: { md: "sticky" },
                        left: { md: 0 },
                        zIndex: { md: 1 },
                        // fontWeight: 500,
                        px: 3,
                        py: 2,
                        minWidth: 260,
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                      }}
                    >
                      {row.feature}
                    </TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col}
                        align="center"
                        sx={{
                          px: 3,
                          py: 2,
                          minWidth: 200,
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                        }}
                      >
                        {"render" in row
                          ? row.render()
                          : row[col] ?? <span className="text-muted">—</span>}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* <div className="mt-4 text-muted small">
        <p className="mb-1">
          Notes: Items marked with <span style={{ color: YES_COLOR }}>✓</span> are included. Items
          marked with <span style={{ color: NO_COLOR }}>✕</span> are not included.
        </p>
      </div> */}
    </div>
  );
}
